import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { propertyTokenGenerator } from '../services/tokenGenerator';
import { updateMetaTags, generatePropertyMetaData, resetMetaTagsToDefault } from '../utils/metaTags';
import { supabase } from '../lib/supabase';
import { getOrCreateClientSessionId } from '../services/imageProcessing';
import { campaignTracking } from '../services/campaignTracking';

const PropertyTokenPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadProperty = async () => {
      if (!token) {
        console.log('❌ No token provided, redirecting to home');
        navigate('/');
        return;
      }

      try {
        console.log('🔍 PropertyTokenPage: Starting load for token:', token);
        
        // Validate token format first (quick check)
        if (!isValidTokenFormat(token)) {
          console.log('❌ Invalid token format:', token);
          console.log('🔄 Redirecting to home page...');
          navigate('/');
          return;
        }

        console.log('✅ Token format is valid:', token);
        console.log('🔍 Loading property for token:', token);
        
        // Try to fetch the property data using universal function that handles both tokens and access codes
        console.log('🔍 Calling bridgeAccessCodeToPropertySearch...');
        let propertyResult = await propertyTokenGenerator.bridgeAccessCodeToPropertySearch(token);
        
        console.log('📊 Bridge function result:', propertyResult ? 'Property found' : 'No property found');
        
        if (propertyResult) {
          let property = propertyResult.property;
          const fromBridge = propertyResult.fromBridge;
          const accessCode = propertyResult.accessCode;
          
          console.log('🎯 Bridge result details:', {
            propertyId: property.id,
            fromBridge,
            accessCode,
            hasWatercolor: !!property.watercolor_image_url,
            hasPencil: !!property.pencil_image_url,
            hasOil: !!property.oil_image_url
          });

          // 🎯 TRACK PROPERTY OPENED EVENT
          console.log('🎯 Tracking property opened event...');
          
          // Check for email campaign UTM parameters
          const urlParams = new URLSearchParams(window.location.search);
          const utmSource = urlParams.get('utm_source');
          const utmCampaign = urlParams.get('utm_campaign');
          
          if (utmSource === 'email-campaign' && utmCampaign) {
            console.log('📧 Email campaign click detected!', { utmSource, utmCampaign });
            console.log('📊 Property data for click tracking:', {
              property_id: property.id,
              customer_email: property.customer_email,
              campaign_name: `${utmCampaign}-clicked`
            });
            
            // Record click in database
            try {
              console.log('🔄 Attempting to insert click record...');
              const { data: clickRecord, error: clickError } = await supabase
                .from('email_campaign_sends')
                .insert({
                  property_search_id: property.id,
                  email: property.customer_email || 'unknown',
                  campaign_name: `${utmCampaign}-clicked`
                })
                .select();
              
              console.log('📝 Insert result:', { data: clickRecord, error: clickError });
              
              if (clickError) {
                console.error('❌ Failed to record email campaign click:', clickError);
                console.error('❌ Error details:', JSON.stringify(clickError, null, 2));
              } else {
                console.log('✅ Email campaign click recorded in database', clickRecord);
              }
            } catch (dbError) {
              console.error('💥 Exception while recording click:', dbError);
            }
            
            // Send Slack notification
            try {
              const slackPayload = {
                text: `🎄 *Holiday Email Campaign Click!*\n\n` +
                      `📧 Campaign: ${utmCampaign}\n` +
                      `🔗 Property Token: ${token}\n` +
                      `🕐 Time: ${new Date().toLocaleString()}\n` +
                      `🌐 Full URL: ${window.location.href}`
              };
              
              // Call Slack webhook (replace with your actual webhook URL)
              const SLACK_WEBHOOK_URL = import.meta.env.VITE_SLACK_WEBHOOK_URL || 'YOUR_SLACK_WEBHOOK_URL';
              
              if (SLACK_WEBHOOK_URL && !SLACK_WEBHOOK_URL.includes('YOUR_SLACK')) {
                await fetch(SLACK_WEBHOOK_URL, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(slackPayload)
                });
                console.log('✅ Slack notification sent for email campaign click');
              }
            } catch (slackError) {
              console.error('Failed to send Slack notification:', slackError);
            }
          }
          
          try {
            // Get campaign info if this is a campaign property
            const campaignInfo = accessCode ? await campaignTracking.getCampaignPropertyInfo(accessCode) : null;
            
            console.log('🎯 [PropertyTokenPage] About to call trackPropertyOpened:', {
              propertyId: property.id,
              accessCode: accessCode || token,
              campaignId: campaignInfo?.campaignId,
              timestamp: new Date().toISOString(),
              location: window.location.href
            });
            
            // Track property opened (configurator access)
            const trackingResult = await campaignTracking.trackPropertyOpened({
              propertyId: property.id,
              campaignId: campaignInfo?.campaignId,
              accessCode: accessCode || token
            });
            
            console.log('✅ [PropertyTokenPage] trackPropertyOpened result:', {
              success: trackingResult,
              propertyId: property.id,
              campaignId: campaignInfo?.campaignId,
              accessCode: accessCode || token,
              isCampaignProperty: !!campaignInfo?.campaignId
            });
          } catch (trackingError) {
            console.warn('⚠️ [PropertyTokenPage] Failed to track property events:', trackingError);
            // Continue anyway - tracking shouldn't block the user experience
          }

          // Check if this is the first time the property is being opened
          if (!property.first_opened_at && !property.image_source) {
            console.log('🔄 First time opening - updating first_opened_at...');
            // Only update first_opened_at if this is not from single image workflow
            const { error: updateError } = await supabase
              .from('property_searches')
              .update({ 
                first_opened_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              })
              .eq('id', property.id);

            if (updateError) {
              console.error('❌ Error updating first_opened_at:', updateError);
              // Continue anyway as this is not critical
            } else {
              console.log('✅ Updated first_opened_at successfully');
            }
          } else if (property.first_opened_at) {
            // Property has been opened before - create a clone
            console.log('🔄 Creating clone of property (already opened):', property.id);
            
            // Generate new temporary token for the clone
            const tempToken = await propertyTokenGenerator.generateTemporaryToken();
            console.log('🎯 Generated temp token for clone:', tempToken);
            
            // Create clone with all existing data
            const { data: clone, error: cloneError } = await supabase
              .from('property_searches')
              .insert([{
                id: crypto.randomUUID(),
                parent_property_id: property.id,
                is_clone: true,
                property_token: tempToken,
                token_generated_at: new Date().toISOString(),
                master_image_url: property.selected_image_url || property.master_image_url,
                selected_image_url: property.selected_image_url,
                watercolor_image_url: property.watercolor_image_url,
                pencil_image_url: property.pencil_image_url,
                oil_image_url: property.oil_image_url,
                charcoal_image_url: property.charcoal_image_url,
                chalk_image_url: property.chalk_image_url,
                address: property.address,
                address_line_1: property.address_line_1,
                address_line_2: property.address_line_2,
                city: property.city,
                state: property.state,
                zip_code: property.zip_code,
                country: property.country,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                first_opened_at: new Date().toISOString(),
                client_session_id: getOrCreateClientSessionId(),
                status: property.status || 'completed'
              }])
              .select()
              .single();

            if (cloneError) {
              console.error('❌ Error creating property clone:', cloneError);
              // If clone fails, proceed with original property
            } else {
              // Use the clone instead of original
              property = clone;
              console.log('✅ Created clone with new token:', tempToken);
            }
          } else {
            console.log('🔄 Property exists but no first_opened_at - using as is');
          }
          
          // Update meta tags for social sharing BEFORE navigation
          try {
            const metaData = generatePropertyMetaData(property, token);
            updateMetaTags(metaData);
            console.log('✅ Updated meta tags for social sharing:', metaData);
          } catch (metaError) {
            console.warn('⚠️ Failed to update meta tags:', metaError);
            // Continue even if meta tag update fails
          }
          
          console.log('🎯 Navigating to configurator with property data...');
          console.log('📦 Navigation state:', {
            propertyId: property.id,
            fromToken: true,
            fromBridge,
            accessCode,
            token: property.property_token
          });
          
          // Navigate to configurator with the property data
          navigate('/configure', { 
            state: { 
              propertyData: property,
              fromToken: true,
              fromBridge,
              accessCode,
              token: property.property_token // Use new token if cloned
            } 
          });
        } else {
          console.log('❌ No property found for token:', token);
          setError(`Property not found for token: ${token}`);
          
          // Redirect to homepage after a brief delay
          setTimeout(() => {
            console.log('🔄 Redirecting to home after error...');
            navigate('/');
          }, 3000);
        }
      } catch (err) {
        console.error('❌ Error loading property for token:', token, err);
        console.error('❌ Error details:', err);
        setError('Error loading property');
        
        // Redirect to homepage after a brief delay
        setTimeout(() => {
          console.log('🔄 Redirecting to home after error...');
          navigate('/');
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    loadProperty();
  }, [token, navigate]);

  // Reset meta tags when component unmounts or when navigating away
  useEffect(() => {
    return () => {
      // Only reset if we're not on a token page (to avoid resetting during navigation within the app)
      if (!window.location.pathname.match(/^\/[A-Z0-9]+$/i)) {
        resetMetaTagsToDefault();
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return null; // Component always redirects
};

// Helper function to validate token format
const isValidTokenFormat = (token: string): boolean => {
  // Pattern for address-based tokens: {number}{letters}{5-digit-zip}{optional-letters}
  const addressPattern = /^\d+[A-Z]+\d{5}[A-Z]*$/i;
  
  // Pattern for temporary tokens: IMG{timestamp}{random-chars}
  const tempPattern = /^IMG[A-Z0-9]+$/i;
  
  // Pattern for access codes from scraped_properties: 8 alphanumeric characters
  const accessCodePattern = /^[A-Z0-9]{8}$/i;
  
  const matchesAddress = addressPattern.test(token);
  const matchesTemp = tempPattern.test(token);
  const matchesAccessCode = accessCodePattern.test(token);
  const valid = matchesAddress || matchesTemp || matchesAccessCode;
  
  console.log('🔍 Token validation details:', { 
    token, 
    valid, 
    matchesAddress, 
    matchesTemp, 
    matchesAccessCode,
    tokenLength: token.length
  });
  
  return valid;
};

export default PropertyTokenPage; 